import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import CostPoolDetailsForm from '../CostPoolDetailModal/sections/CostPoolDetailsForm';
import CostPoolPaymentMethods from '../CostPoolDetailModal/sections/CostPoolPaymentMethods';
import CostPoolSettings from '../CostPoolDetailModal/sections/CostPoolSettings';
import UsageBillingTab from '../CostPoolDetailModal/sections/UsageBillingTab/UsageBillingTab';
import WizardHeader from './components/WizardHeader';
import SegmentedControl from './components/SegmentedControl';
import { getApiClient } from 'utils/apiClient';
import { useWidgetVisibility } from 'hooks/useWidgetVisibility';
import { useSearchParamSync } from 'hooks/useSearchParamSync';
import './CostPoolFullPageModal.css';
import './components/SegmentedControl.css';
import { uiAccountTypeFromCostPool } from 'utils/costPoolUiAccountType';

const CostPoolFullPageModal = ({ 
  isOpen, 
  onClose, 
  costPoolId, 
  mode: initialMode = 'view', 
  tenantId, 
  availableTenants = [],
  tenantDetails = {},
  onNavigateToTenant,
  onSuccess 
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, handleTabChange] = useSearchParamSync('costpool_tab', searchParams, setSearchParams, {
    default: 'details',
    normalizeMap: { subscriptions: 'billing' }
  });

  // Tab order for swipe navigation (subscriptions removed)
  const tabs = ['details', 'payment-methods', 'billing', 'settings'];

  // Always use 'edit' mode when viewing (not creating) - no special view mode
  const mode = initialMode === 'create' ? 'create' : 'edit';
  
  // Swipe navigation handlers using refs (similar to other components)
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const contentRef = useRef(null);
  
  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;
  
  // Swipe handlers for mobile tab navigation
  useEffect(() => {
    // Only enable swipe in view/edit mode, not create mode
    if (mode === 'create' || !contentRef.current) return;
    
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
    };
    
    const handleTouchMove = (e) => {
      touchEndX.current = e.touches[0].clientX;
    };
    
    const handleTouchEnd = () => {
      if (!touchStartX.current || !touchEndX.current) {
        touchStartX.current = 0;
        touchEndX.current = 0;
        return;
      }
      
      const distance = touchStartX.current - touchEndX.current;
      
      if (Math.abs(distance) > minSwipeDistance) {
        const currentIndex = tabs.indexOf(activeTab);
        
        if (distance > 0) {
          // Swipe left - go to next tab
          const nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : currentIndex;
          if (nextIndex !== currentIndex) {
            handleTabChange(tabs[nextIndex]);
          }
        } else {
          // Swipe right - go to previous tab
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
          if (prevIndex !== currentIndex) {
            handleTabChange(tabs[prevIndex]);
          }
        }
      }
      
      // Reset
      touchStartX.current = 0;
      touchEndX.current = 0;
    };
    
    const element = contentRef.current;
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [mode, activeTab, handleTabChange, tabs]);
  
  const [wizardStep, setWizardStep] = useState(1);
  const [accountType, setAccountType] = useState('business'); // Default to business since Personal is disabled
  const [costPool, setCostPool] = useState(null);
  const [, setLoadingCostPool] = useState(false);

  // Ensure business is selected when opening in create mode
  useEffect(() => {
    if (mode === 'create' && isOpen) {
      setAccountType('business');
    }
  }, [mode, isOpen]);

  // Load cost pool data to get account type and tenant info
  useEffect(() => {
    if (mode !== 'create' && costPoolId && tenantId) {
      const loadCostPool = async () => {
        try {
          setLoadingCostPool(true);
          const apiClient = getApiClient();
          const response = await apiClient.getCostPool(costPoolId, tenantId);
          if (response.success && response.data) {
            setCostPool(response.data);
            setAccountType(uiAccountTypeFromCostPool(response.data));
          }
        } catch (err) {
          console.error('Error loading cost pool:', err);
        } finally {
          setLoadingCostPool(false);
        }
      };
      loadCostPool();
    }
  }, [mode, costPoolId, tenantId]);

  // Handler for account type change in header
  const handleAccountTypeChange = useCallback(async (newType) => {
    if (mode === 'create') {
      setAccountType(newType);
      return;
    }

    // For edit mode, save immediately
    if (!costPoolId || !tenantId) return;

    try {
      const apiClient = getApiClient();
      const updateData = {
        account_type: newType === 'personal' ? 'individual' : 'business'
      };
      
      // If switching to business, validate required fields
      const errors = {};
      if (newType === 'business' && costPool) {
        if (!costPool.legal_form?.trim()) {
          errors.legal_form = 'Legal form is required for business accounts';
        }
        if (!costPool.vat_id?.trim()) {
          errors.vat_id = 'VAT ID is required for business accounts';
        }
      }
      
      if (Object.keys(errors).length > 0) {
        alert('Please fill in required business information (Legal Form and VAT ID) before switching to business account type.');
        return;
      }
      
      const response = await apiClient.updateCostPool(costPoolId, { ...updateData, tenant_id: tenantId });
      if (response.success) {
        setAccountType(newType);
        // Reload cost pool to get updated data
        const reloadResponse = await apiClient.getCostPool(costPoolId, tenantId);
        if (reloadResponse.success && reloadResponse.data) {
          setCostPool(reloadResponse.data);
        }
        if (onSuccess) {
          onSuccess('costPools');
        }
      } else {
        alert(response.error || 'Failed to update cost pool type');
      }
    } catch (err) {
      console.error('Error updating account type:', err);
      alert(`Error updating cost pool type: ${err.message}`);
    }
  }, [mode, costPoolId, tenantId, costPool, onSuccess]);

  // Hide widget when modal is open (handled by centralized hook)
  useWidgetVisibility(isOpen);

  if (!isOpen) return null;

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation();
    }
    // Reset all state
    handleTabChange('details');
    setWizardStep(1);
    setAccountType('business'); // Default to business since Personal is disabled
    // Close the modal
    if (onClose) {
      onClose();
    }
  };

  const modalContent = (
    <div 
      className="costpool-modal-overlay" 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className="costpool-modal-container fullscreen-mode"
        onClick={(e) => e.stopPropagation()}
        data-onboarding-target="costpool-modal"
      >
        {mode === 'create' ? (
          <>
            <div className="wizard-header-wrapper">
              <WizardHeader
                currentStep={wizardStep}
                totalSteps={3}
                accountType={accountType}
                onAccountTypeChange={setAccountType}
                title="Create Cost Pool"
                subtitle="Set up your billing account in just a few steps"
              />
            </div>
            <button 
              type="button"
              className="costpool-modal-close wizard-close-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent?.stopImmediatePropagation();
                handleClose(e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              aria-label="Close"
            >×</button>
          </>
        ) : (
          <div className="view-edit-header-wrapper" style={{ position: 'relative' }}>
            <div className="costpool-modal-header">
              <div className="costpool-modal-header-title">
                <h3 className="costpool-modal-title">
                  Cost Pool Details
                </h3>
                {costPoolId && (
                  <p className="costpool-modal-subtitle">
                    Manage your billing account and payment methods
                  </p>
                )}
              </div>
              <div className="costpool-modal-header-actions">
                <div className="costpool-modal-header-context">
                  <div className="header-tenant-indicator">
                    <span className="tenant-label">Tenant: </span>
                    <span className="tenant-name">
                      {tenantDetails[tenantId]?.name || availableTenants.find(t => (typeof t === 'string' ? t : t.id) === tenantId)?.name || tenantId || 'Unknown'}
                    </span>
                  </div>
                  <span className="header-context-divider" aria-hidden="true" />
                  <div className="header-cost-pool-type">
                    <span className="type-label">Account Type: </span>
                    <SegmentedControl
                      value={accountType}
                      onChange={handleAccountTypeChange}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="costpool-modal-tabs">
              <button
                type="button"
                className={`costpool-tab ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => handleTabChange('details')}
              >
                Details
              </button>
              <button
                type="button"
                className={`costpool-tab ${activeTab === 'payment-methods' ? 'active' : ''}`}
                onClick={() => handleTabChange('payment-methods')}
              >
                Payment Methods
              </button>
              <button
                type="button"
                className={`costpool-tab ${activeTab === 'billing' ? 'active' : ''}`}
                onClick={() => handleTabChange('billing')}
              >
                Billing & Usage
              </button>
              <button
                type="button"
                className={`costpool-tab ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => handleTabChange('settings')}
              >
                Settings
              </button>
            </div>
            <button 
              type="button"
              className="costpool-modal-close wizard-close-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent?.stopImmediatePropagation();
                handleClose(e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              aria-label="Close"
            >×</button>
          </div>
        )}

        <div className="costpool-modal-content" ref={contentRef}>
          {mode === 'create' ? (
            <CostPoolDetailsForm
              mode="create"
              tenantId={tenantId}
              availableTenants={availableTenants}
              tenantDetails={tenantDetails}
              accountType={accountType}
              onAccountTypeChange={setAccountType}
              wizardStep={wizardStep}
              onWizardStepChange={setWizardStep}
              onSuccess={(result) => {
                if (onSuccess) {
                  onSuccess(result);
                }
                handleClose();
              }}
              onCancel={handleClose}
            />
          ) : (
            <>
              {activeTab === 'details' && (
                <CostPoolDetailsForm
                  mode="edit"
                  costPoolId={costPoolId}
                  tenantId={tenantId}
                  availableTenants={availableTenants}
                  tenantDetails={tenantDetails}
                  onNavigateToTenant={onNavigateToTenant}
                  accountType={accountType}
                  onAccountTypeChange={handleAccountTypeChange}
                  onSuccess={() => {
                    if (onSuccess) {
                      onSuccess('costPools');
                    }
                    // Reload cost pool data after changes
                    if (costPoolId && tenantId) {
                      const reloadCostPool = async () => {
                        try {
                          const apiClient = getApiClient();
                          const response = await apiClient.getCostPool(costPoolId, tenantId);
                          if (response.success && response.data) {
                            setCostPool(response.data);
                            setAccountType(uiAccountTypeFromCostPool(response.data));
                          }
                        } catch (err) {
                          console.error('Error reloading cost pool:', err);
                        }
                      };
                      reloadCostPool();
                    }
                  }}
                />
              )}
              {activeTab === 'payment-methods' && (
                <CostPoolPaymentMethods
                  costPoolId={costPoolId}
                  tenantId={tenantId}
                  activeTab={activeTab}
                  dashboardTab={searchParams.get('tab') || 'cost-pool'}
                />
              )}
              {activeTab === 'billing' && (
                <div className="costpool-billing-section">
                  <UsageBillingTab
                    costPools={costPool ? [costPool] : []}
                    fixedCostPoolId={costPoolId}
                    hideCostPoolSelector={true}
                  />
                </div>
              )}
              {activeTab === 'settings' && (
                <CostPoolSettings
                  costPoolId={costPoolId}
                  tenantId={tenantId}
                  onSuccess={() => {
                    if (onSuccess) {
                      onSuccess('costPools');
                    }
                  }}
                  onCancel={handleClose}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CostPoolFullPageModal;

